/**
 * Serial Port Service for connecting to SkimBuster device
 * Handles device communication and training data collection
 */

import { ScanData, ProfileCategory } from '../types/cardReaderProfiles';

// Web Serial API type definitions
declare global {
  interface Navigator {
    serial: Serial;
  }

  interface Serial {
    requestPort(options?: SerialPortRequestOptions): Promise<SerialPort>;
    getPorts(): Promise<SerialPort[]>;
  }

  interface SerialPortRequestOptions {
    filters?: SerialPortFilter[];
  }

  interface SerialPortFilter {
    usbVendorId?: number;
    usbProductId?: number;
  }

  interface SerialPort {
    open(options: SerialOptions): Promise<void>;
    close(): Promise<void>;
    getInfo(): SerialPortInfo;
    readable: ReadableStream<Uint8Array> | null;
    writable: WritableStream<Uint8Array> | null;
  }

  interface SerialOptions {
    baudRate: number;
    dataBits?: number;
    stopBits?: number;
    parity?: 'none' | 'even' | 'odd';
    flowControl?: 'none' | 'hardware';
  }

  interface SerialPortInfo {
    usbVendorId?: number;
    usbProductId?: number;
  }
}

// Serial port connection state
export interface SerialPortConnection {
  port: SerialPort | null;
  reader: ReadableStreamDefaultReader | null;
  writer: WritableStreamDefaultWriter | null;
  isConnected: boolean;
  deviceInfo?: {
    productName?: string;
    manufacturerName?: string;
    serialNumber?: string;
  };
}

// Training session for data collection
export interface LiveTrainingSession {
  id: string;
  profileName: string;
  category: ProfileCategory;
  targetScans: number;
  collectedScans: number;
  scans: ScanData[];
  startTime: Date;
  status: 'connecting' | 'collecting' | 'completed' | 'error';
  deviceId: string;
  lastScanTime?: Date;
}

// Parsed scan data from device
export interface ParsedScanData {
  scanId: string;
  timestamp: Date;
  sensorReadings: number[];
  duration: number;
  rawData: string;
}

export class SerialPortService {
  private static connection: SerialPortConnection = {
    port: null,
    reader: null,
    writer: null,
    isConnected: false
  };

  private static dataBuffer: string = '';
  private static scanCallbacks: ((scan: ParsedScanData) => void)[] = [];
  private static logCallbacks: ((message: string) => void)[] = [];

  /**
   * Check if Web Serial API is supported
   */
  static isSupported(): boolean {
    return 'serial' in navigator;
  }

  /**
   * Request and connect to SkimBuster device
   */
  static async connect(): Promise<boolean> {
    if (!this.isSupported()) {
      throw new Error('Web Serial API is not supported in this browser');
    }

    try {
      console.log('🔌 Requesting serial port access...');
      
      // Request port with SkimBuster device filters
      const port = await navigator.serial.requestPort({
        filters: [
          { usbVendorId: 0x303a, usbProductId: 0x1001 }, // ESP32-S3 generic
          { usbVendorId: 0x10c4, usbProductId: 0xea60 }, // CP2102 USB-UART
        ]
      });

      // Get device info
      const info = port.getInfo();
      console.log('📱 Device info:', info);

      // Open port with SkimBuster settings
      await port.open({
        baudRate: 115200,
        dataBits: 8,
        stopBits: 1,
        parity: 'none',
        flowControl: 'none'
      });

      this.connection.port = port;
      this.connection.isConnected = true;
      this.connection.deviceInfo = {
        productName: 'SkimBuster Device',
        serialNumber: `SB_${Date.now().toString(36)}`
      };

      // Set up data streams
      await this.setupDataStreams();

      console.log('✅ Connected to SkimBuster device');
      return true;

    } catch (error) {
      console.error('❌ Failed to connect to device:', error);
      this.connection.isConnected = false;
      throw error;
    }
  }

  /**
   * Disconnect from device
   */
  static async disconnect(): Promise<void> {
    try {
      console.log('🔌 Disconnecting from device...');

      // Close streams
      if (this.connection.reader) {
        await this.connection.reader.cancel();
        this.connection.reader.releaseLock();
        this.connection.reader = null;
      }

      if (this.connection.writer) {
        await this.connection.writer.close();
        this.connection.writer = null;
      }

      // Close port
      if (this.connection.port) {
        await this.connection.port.close();
        this.connection.port = null;
      }

      this.connection.isConnected = false;
      this.dataBuffer = '';

      console.log('✅ Disconnected from device');
    } catch (error) {
      console.error('❌ Error disconnecting:', error);
    }
  }

  /**
   * Check if device is connected
   */
  static isConnected(): boolean {
    return this.connection.isConnected && this.connection.port !== null;
  }

  /**
   * Get connection info
   */
  static getConnectionInfo(): SerialPortConnection {
    return { ...this.connection };
  }

  /**
   * Set device to profile training mode
   */
  static async enterTrainingMode(): Promise<void> {
    if (!this.isConnected()) {
      throw new Error('Device not connected');
    }

    await this.sendCommand('mode profile');
    await this.sendCommand('send_scan_data on');
    console.log('📚 Device set to training mode');
  }

  /**
   * Exit training mode
   */
  static async exitTrainingMode(): Promise<void> {
    if (!this.isConnected()) {
      return;
    }

    await this.sendCommand('send_scan_data off');
    await this.sendCommand('mode standalone');
    console.log('🏁 Device exited training mode');
  }

  /**
   * Send command to device
   */
  private static async sendCommand(command: string): Promise<void> {
    if (!this.connection.writer) {
      throw new Error('Writer not available');
    }

    const encoder = new TextEncoder();
    const data = encoder.encode(command + '\n');
    
    console.log('📤 Sending command:', command);
    this.emitLogMessage(`COMMAND: ${command}`);
    await this.connection.writer.write(data);
  }

  /**
   * Set up data reading streams
   */
  private static async setupDataStreams(): Promise<void> {
    if (!this.connection.port) {
      throw new Error('Port not available');
    }

    // Set up reader
    this.connection.reader = this.connection.port.readable?.getReader() || null;
    this.connection.writer = this.connection.port.writable?.getWriter() || null;

    if (this.connection.reader) {
      // Start reading data
      this.startReading();
    }
  }

  /**
   * Start reading data from device
   */
  private static async startReading(): Promise<void> {
    const decoder = new TextDecoder();

    try {
      while (this.connection.reader && this.connection.isConnected) {
        const { value, done } = await this.connection.reader.read();
        
        if (done) {
          break;
        }

        if (value) {
          const text = decoder.decode(value);
          this.dataBuffer += text;
          this.processBuffer();
        }
      }
    } catch (error) {
      if (this.connection.isConnected) {
        console.error('❌ Error reading from device:', error);
      }
    }
  }

  /**
   * Process incoming data buffer
   */
  private static processBuffer(): void {
    const lines = this.dataBuffer.split('\n');
    this.dataBuffer = lines.pop() || ''; // Keep incomplete line

    for (const line of lines) {
      this.processLine(line.trim());
    }
  }

  /**
   * Process individual line of data
   */
  private static processLine(line: string): void {
    // Always emit raw data to log (before filtering)
    this.emitLogMessage(`RAW: ${line}`);
    
    // Skip empty lines and debug messages
    if (!line || line.startsWith('DEBUG') || line.startsWith('INFO') || line.startsWith('🔧') || line.startsWith('⚡')) {
      return;
    }

    console.log('📡 Raw data:', line);

    // Check for scan start trigger
    if (line.includes('🔥 TRIGGERED:')) {
      this.emitLogMessage(`SCAN START: Trigger detected`);
      // Don't create scan yet, wait for first sensor data
      return;
    }

    // Parse sensor data from firmware (format: 📡 timestamp,data1,data2,data3,...)
    // Also handle corrupted emoji prefixes (□□□ or other corruption)
    // Handle interrupted lines that start with comma (missing prefix)
    const isSensorLine = line.startsWith('📡') || 
                        line.match(/^[□■�]{1,3}\s*\d+,/) || // Corrupted emoji patterns
                        line.match(/^.{1,4}\s*\d+,.*,\d+,\d+,\d+$/) || // Any prefix + timestamp,data...,CH0,CH1,shift
                        line.match(/^,\d+(\.\d+)?,.*,\d+,\d+,-?\d+$/); // Interrupted line starting with comma
    
    if (isSensorLine) {
      this.emitLogMessage(`SENSOR DATA: Found sensor line (prefix: ${line.substring(0, 5)})`);
      try {
        const sensorData = this.parseFirmwareSensorLine(line);
        if (sensorData) {
          this.emitLogMessage(`PARSED: CH0=${sensorData.ch0Value}, CH1=${sensorData.ch1Value}, CH1Shift=${sensorData.ch1Shift}`);
          // Collect sensor readings for current scan
          this.collectSensorReading(sensorData);
        } else {
          this.emitLogMessage(`PARSE FAILED: Could not parse sensor data`);
        }
      } catch (error) {
        console.error('❌ Error parsing sensor data:', error);
        this.emitLogMessage(`ERROR: ${(error as Error).message}`);
      }
    }
    
    // Check for scan end point - only CONTINUOUS DATA COLLECTION ENDED
    if (line.includes('📋 CONTINUOUS DATA COLLECTION ENDED')) {
      this.emitLogMessage(`SCAN END: Data collection ended`);
      this.finalizeScan();
    }
  }

  /**
   * Parse scan data line
   */
  private static parseScanLine(line: string): ParsedScanData | null {
    // Format: D::timestamp::data1,data2,data3...
    const parts = line.split('::');
    
    if (parts.length < 3) {
      return null;
    }

    const timestampStr = parts[1];
    const dataStr = parts[2];

    // Parse sensor data
    const sensorReadings = dataStr.split(',')
      .map(val => parseFloat(val.trim()))
      .filter(val => !isNaN(val));

    if (sensorReadings.length === 0) {
      return null;
    }

    // Calculate duration (assume 1ms per sample)
    const duration = sensorReadings.length;

    return {
      scanId: `scan_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      sensorReadings,
      duration,
      rawData: line
    };
  }

  // Current scan data collection - matching mobile app ScanData format
  private static currentScanData: {
    timestamp: Date;
    scanId: string;
    coilData: {
      timestamps: number[];
      ch0Values: number[];
      ch1Values: number[];
      ch1Shift: number[];
    };
    lastDataTime: Date;
  } | null = null;
  
  private static scanTimeoutTimer: NodeJS.Timeout | null = null;
  private static scanFinalizing: boolean = false;
  private static lastFinalizedScanId: string | null = null;

  /**
   * Parse firmware sensor data line
   * Format: 📡 timestamp,data1,data2,...,CH0,CH1,shift
   */
  private static parseFirmwareSensorLine(line: string): { 
    timestamp: number; 
    ch0Value: number;
    ch1Value: number;
    ch1Shift: number;
  } | null {
    let dataStr: string;
    let timestamp: number | undefined;
    
    // Handle interrupted lines that start with comma (missing timestamp)
    if (line.match(/^,\d+(\.\d+)?,.*,\d+,\d+,-?\d+$/)) {
      // Interrupted line: ,179.62,7,50156,2,8,273663,280063,11776
      // Estimate timestamp from current scan data
      const parts = line.substring(1).split(','); // Remove leading comma
      if (this.currentScanData && this.currentScanData.coilData.timestamps.length > 0) {
        const lastTimestamp = this.currentScanData.coilData.timestamps[this.currentScanData.coilData.timestamps.length - 1];
        timestamp = lastTimestamp + 5; // Estimate next timestamp (+5ms)
        dataStr = `${timestamp},${line.substring(1)}`; // Reconstruct full line
        this.emitLogMessage(`RECOVERED: Interrupted line reconstructed with estimated timestamp ${timestamp}`);
      } else {
        this.emitLogMessage(`PARSE FAILED: Interrupted line with no previous timestamp reference: ${line}`);
        return null;
      }
    } else {
      // Normal parsing - find the first digit to start parsing from
      const match = line.match(/(\d+,.*)/); // Find timestamp,data... pattern
      if (!match) {
        this.emitLogMessage(`PARSE FAILED: No timestamp pattern found in: ${line}`);
        return null;
      }
      dataStr = match[1]; // Everything from first digit onward
    }
    
    const parts = dataStr.split(',');
    
    // More lenient check: need at least 4 parts (timestamp + CH0 + CH1 + shift)
    if (parts.length < 4) {
      this.emitLogMessage(`PARSE FAILED: Too few parts (${parts.length} < 4): ${line}`);
      return null;
    }

    // Parse timestamp if not already set
    if (timestamp === undefined) {
      timestamp = parseFloat(parts[0].trim());
      if (isNaN(timestamp)) {
        this.emitLogMessage(`PARSE FAILED: Invalid timestamp: ${parts[0]}`);
        return null;
      }
    }

    // Extract the last 3 values: CH0, CH1, shift
    const ch0Value = parseFloat(parts[parts.length - 3].trim());
    const ch1Value = parseFloat(parts[parts.length - 2].trim());
    const ch1Shift = parseFloat(parts[parts.length - 1].trim());

    if (isNaN(ch0Value) || isNaN(ch1Value) || isNaN(ch1Shift)) {
      this.emitLogMessage(`PARSE FAILED: Invalid CH values - CH0:${parts[parts.length - 3]} CH1:${parts[parts.length - 2]} Shift:${parts[parts.length - 1]}`);
      return null;
    }

    this.emitLogMessage(`PARSE SUCCESS: ${parts.length} parts, CH0=${ch0Value}, CH1=${ch1Value}, CH1Shift=${ch1Shift}`);
    return { timestamp, ch0Value, ch1Value, ch1Shift };
  }

  /**
   * Collect sensor reading for current scan
   */
  private static collectSensorReading(sensorData: { 
    timestamp: number; 
    ch0Value: number;
    ch1Value: number;
    ch1Shift: number;
  }): void {
    // Don't collect data if we're finalizing a scan
    if (this.scanFinalizing) {
      this.emitLogMessage('SKIPPING: Sensor data ignored during scan finalization');
      return;
    }
    
    const now = new Date();
    
    // Initialize new scan if needed
    if (!this.currentScanData) {
      this.currentScanData = {
        timestamp: now,
        scanId: `scan_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        coilData: {
          timestamps: [],
          ch0Values: [],
          ch1Values: [],
          ch1Shift: []
        },
        lastDataTime: now
      };
      console.log('🆕 Started collecting new scan:', this.currentScanData.scanId);
      this.emitLogMessage(`NEW SCAN: Started ${this.currentScanData.scanId}`);
    }

    // Add sensor readings to current scan (mobile app format)
    this.currentScanData.coilData.timestamps.push(sensorData.timestamp);
    this.currentScanData.coilData.ch0Values.push(sensorData.ch0Value);
    this.currentScanData.coilData.ch1Values.push(sensorData.ch1Value);
    this.currentScanData.coilData.ch1Shift.push(sensorData.ch1Shift);
    this.currentScanData.lastDataTime = now;
    
    // Clear existing timeout
    if (this.scanTimeoutTimer) {
      clearTimeout(this.scanTimeoutTimer);
    }
    
    // Set new timeout to finalize scan after 2 seconds of no data
    this.scanTimeoutTimer = setTimeout(() => {
      if (this.currentScanData) {
        this.emitLogMessage(`TIMEOUT: No new data for 2 seconds, finalizing scan`);
        this.finalizeScan();
      }
    }, 2000);
  }

  /**
   * Finalize current scan and notify callbacks
   */
  private static finalizeScan(): void {
    // Prevent duplicate finalization
    if (this.scanFinalizing) {
      this.emitLogMessage('WARNING: Scan already finalizing, ignoring duplicate call');
      return;
    }
    
    if (!this.currentScanData) {
      console.log('⚠️ No scan data to finalize');
      this.emitLogMessage('WARNING: No scan data to finalize');
      return;
    }
    
    // Check if we already finalized this scan
    if (this.lastFinalizedScanId === this.currentScanData.scanId) {
      this.emitLogMessage(`WARNING: Scan ${this.currentScanData.scanId} already finalized, ignoring duplicate`);
      return;
    }
    
    this.scanFinalizing = true;
    
    // Clear timeout if it exists
    if (this.scanTimeoutTimer) {
      clearTimeout(this.scanTimeoutTimer);
      this.scanTimeoutTimer = null;
    }

    // Calculate actual duration from first to last timestamp
    const firstTimestamp = this.currentScanData.coilData.timestamps[0] || 0;
    const lastTimestamp = this.currentScanData.coilData.timestamps[this.currentScanData.coilData.timestamps.length - 1] || 0;
    const actualDuration = lastTimestamp - firstTimestamp; // Duration in milliseconds from firmware timestamps
    
    const scanData: ParsedScanData = {
      scanId: this.currentScanData.scanId,
      timestamp: this.currentScanData.timestamp,
      // For compatibility with existing ParsedScanData interface, use ch1Shift as sensorReadings
      // This is what the mobile app actually uses for FlowMatch processing
      sensorReadings: this.currentScanData.coilData.ch1Shift,
      duration: actualDuration,
      rawData: `Scan completed with ${this.currentScanData.coilData.ch1Shift.length} ch1Shift readings (${actualDuration}ms)`
    };

    console.log(`✅ Scan completed: ${scanData.scanId} with ${scanData.sensorReadings.length} ch1Shift readings`);
    this.emitLogMessage(`SCAN COMPLETE: ${scanData.scanId} - ${scanData.sensorReadings.length} readings`);
    
    // Track this scan as finalized
    this.lastFinalizedScanId = scanData.scanId;
    
    // Notify all callbacks
    this.scanCallbacks.forEach(callback => callback(scanData));
    
    // Reset for next scan
    this.currentScanData = null;
    this.scanFinalizing = false;
  }

  /**
   * Subscribe to scan data
   */
  static onScanData(callback: (scan: ParsedScanData) => void): () => void {
    this.scanCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.scanCallbacks.indexOf(callback);
      if (index > -1) {
        this.scanCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to serial port log messages
   */
  static onLogMessage(callback: (message: string) => void): () => void {
    this.logCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.logCallbacks.indexOf(callback);
      if (index > -1) {
        this.logCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Emit log message to all subscribers
   */
  private static emitLogMessage(message: string): void {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = `[${timestamp}] ${message}`;
    this.logCallbacks.forEach(callback => callback(logEntry));
  }

  /**
   * Start training session
   */
  static async startTrainingSession(
    profileName: string,
    category: ProfileCategory,
    targetScans: number = 100
  ): Promise<LiveTrainingSession> {
    if (!this.isConnected()) {
      throw new Error('Device not connected');
    }

    const session: LiveTrainingSession = {
      id: `session_${Date.now()}`,
      profileName,
      category,
      targetScans,
      collectedScans: 0,
      scans: [],
      startTime: new Date(),
      status: 'connecting',
      deviceId: this.connection.deviceInfo?.serialNumber || 'unknown'
    };

    // Enter training mode
    await this.enterTrainingMode();
    session.status = 'collecting';

    console.log('🎯 Training session started:', {
      profileName,
      category,
      targetScans
    });

    return session;
  }

  /**
   * Process scan data into ScanData format
   * Maps the collected ch1Shift data (which is what mobile app uses for FlowMatch) to sensorReadings
   */
  static convertToScanData(parsed: ParsedScanData, deviceId: string): ScanData {
    return {
      timestamp: parsed.timestamp,
      sensorReadings: parsed.sensorReadings, // This is already ch1Shift data from finalizeScan
      duration: parsed.duration,
      deviceId,
      scanId: parsed.scanId,
      metadata: {
        signalStrength: parsed.sensorReadings.length
      }
    };
  }

  /**
   * Get available serial ports (for development/debugging)
   */
  static async getAvailablePorts(): Promise<SerialPort[]> {
    if (!this.isSupported()) {
      return [];
    }

    try {
      return await navigator.serial.getPorts();
    } catch (error) {
      console.error('Error getting ports:', error);
      return [];
    }
  }
}